// Auth state, exposed as a module-level singleton.
//
// Deliberately not a factory: there is exactly one signed-in user per tab, and
// every consumer (router guard, top bar, views) must observe the same object.
// A per-call factory would give the guard a different `user` ref than the view.

import {
  EmailAuthProvider,
  browserLocalPersistence,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import { computed, readonly, ref } from 'vue'

import { auth, db } from '../firebase'

export interface Claims {
  active: boolean
  tier:   string
  role:   'admin' | null
}

const EMPTY_CLAIMS: Claims = { active: false, tier: 'commercial', role: null }

/** Client-side mirror of the server's MIN_PASSWORD_LENGTH. Kept in sync by hand. */
export const MIN_PASSWORD_LENGTH = 12

/**
 * sessionStorage key holding the profile the user picked in this tab, so a
 * mid-session reload doesn't re-prompt them. Shared with AppView; cleared on
 * sign-out below.
 */
export const SELECTED_PROFILE_KEY = 'sketcher.selectedProfileId'

const user   = ref<User | null>(null)
const claims = ref<Claims>({ ...EMPTY_CLAIMS })

// Mirror of users/{uid}.mustChangePassword. Kept in sync by a snapshot
// listener while signed in; cleared on sign-out. Undefined and missing fields
// both resolve to false, so pre-existing users without the field aren't forced.
const mustChangePassword = ref(false)
let userDocUnsub: (() => void) | null = null

/**
 * Resolves once Firebase has restored (or definitively failed to restore) any
 * persisted session.
 *
 * The router guard MUST await this. Firebase's initial state is `null` while it
 * reads from IndexedDB, so a guard that reads `user` synchronously bounces a
 * hard refresh on /app to the landing page before the session loads.
 */
let resolveReady: () => void
export const authReady = new Promise<void>((resolve) => {
  resolveReady = resolve
})
let readyResolved = false

setPersistence(auth, browserLocalPersistence).catch(() => {
  // Falls back to in-memory persistence — sessions won't survive a reload, but
  // the app still works. Not worth failing startup over.
})

// onIdTokenChanged rather than onAuthStateChanged: it also fires on token
// refresh (hourly, and after setCustomUserClaims), so a user promoted or
// deactivated by an admin picks up new claims without a full sign-out.
onIdTokenChanged(auth, async (u) => {
  user.value = u
  if (u) {
    try {
      const token = await u.getIdTokenResult()
      claims.value = {
        active: token.claims.active === true,
        tier:   typeof token.claims.tier === 'string' ? token.claims.tier : 'commercial',
        role:   token.claims.role === 'admin' ? 'admin' : null,
      }
    } catch {
      claims.value = { ...EMPTY_CLAIMS }
    }
    subscribeUserDoc(u.uid)
  } else {
    claims.value = { ...EMPTY_CLAIMS }
    unsubscribeUserDoc()
    mustChangePassword.value = false
  }
  if (!readyResolved) {
    readyResolved = true
    resolveReady()
  }
})

function subscribeUserDoc(uid: string) {
  unsubscribeUserDoc()
  userDocUnsub = onSnapshot(
    doc(db, 'users', uid),
    (snap) => { mustChangePassword.value = snap.get('mustChangePassword') === true },
    // A read failure (rules regression, offline) shouldn't lock the user out
    // of the app forever — fail open on the *flag*, not on auth itself.
    () => { mustChangePassword.value = false },
  )
}

function unsubscribeUserDoc() {
  if (userDocUnsub) {
    userDocUnsub()
    userDocUnsub = null
  }
}

/** Maps Firebase error codes to messages safe to show a user. */
export function authErrorMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      // Deliberately identical for all four: distinguishing "no such user" from
      // "wrong password" tells an attacker which emails are registered.
      return 'Incorrect email or password.'
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your administrator.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.'
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.'
    default:
      return 'Something went wrong signing in. Try again.'
  }
}

/**
 * Result of a password-change attempt.
 *
 * The forced flow returns `{ step: 'reauth', code: 'needs-reauth' }` when
 * Firebase rejects the update with `auth/requires-recent-login` — the caller
 * should then prompt for the current password and retry via `changePassword`.
 */
export type ChangePasswordResult =
  | { ok: true }
  | { ok: false; step: 'reauth' | 'update' | 'flag'; code: string }

async function clearMustChangePassword(uid: string): Promise<void> {
  await setDoc(doc(db, 'users', uid), { mustChangePassword: false }, { merge: true })
}

export function useAuth() {
  const isAuthed = computed(() => user.value !== null)
  const isActive = computed(() => user.value !== null && claims.value.active)
  const isAdmin  = computed(() => isActive.value && claims.value.role === 'admin')
  const email    = computed(() => user.value?.email ?? null)

  async function login(emailAddr: string, password: string): Promise<void> {
    const cred = await signInWithEmailAndPassword(auth, emailAddr, password)

    // An account can authenticate successfully and still be deactivated —
    // `active` is a custom claim, not an Auth-level flag. Sign such a user
    // straight back out so no half-authenticated state lingers.
    const token = await cred.user.getIdTokenResult()
    if (token.claims.active !== true) {
      await signOut(auth)
      const err = new Error('account-inactive') as Error & { code: string }
      err.code = 'app/account-inactive'
      throw err
    }
  }

  async function logout(): Promise<void> {
    // The profile picker caches its choice in sessionStorage so a mid-session
    // reload doesn't re-prompt. On sign-out that cache belongs to the previous
    // user — leaving it would leak the choice into the next sign-in on this
    // tab. Best-effort: browsers can throw here under private-mode quotas.
    try { sessionStorage.removeItem(SELECTED_PROFILE_KEY) } catch { /* ignore */ }
    await signOut(auth)
  }

  async function resetPassword(emailAddr: string): Promise<void> {
    await sendPasswordResetEmail(auth, emailAddr)
  }

  /**
   * Voluntary in-app password change. Reauthenticates first — Firebase requires
   * a recent sign-in for updatePassword, and doing it explicitly lets us give
   * "wrong current password" its own error surface distinct from update errors.
   */
  async function changePassword(current: string, next: string): Promise<ChangePasswordResult> {
    const u = user.value
    if (!u || !u.email) return { ok: false, step: 'reauth', code: 'not-signed-in' }

    try {
      const credential = EmailAuthProvider.credential(u.email, current)
      await reauthenticateWithCredential(u, credential)
    } catch (e) {
      return { ok: false, step: 'reauth', code: (e as { code?: string }).code ?? 'unknown' }
    }

    try {
      await updatePassword(u, next)
    } catch (e) {
      return { ok: false, step: 'update', code: (e as { code?: string }).code ?? 'unknown' }
    }

    try {
      await clearMustChangePassword(u.uid)
    } catch (e) {
      // Password update succeeded — don't fail the whole flow. The listener
      // will pick up a stale `true` and re-show the dialog on next login,
      // which is annoying but not incorrect.
      return { ok: false, step: 'flag', code: (e as { code?: string }).code ?? 'unknown' }
    }

    return { ok: true }
  }

  /**
   * Forced password change on first sign-in. Attempts updatePassword directly
   * to spare freshly-logged-in users a second password prompt; if Firebase
   * rejects with `auth/requires-recent-login` (session older than its sensitive
   * window), the caller falls back to prompting for the current password and
   * calling `changePassword`.
   */
  async function changePasswordForced(next: string): Promise<ChangePasswordResult> {
    const u = user.value
    if (!u) return { ok: false, step: 'update', code: 'not-signed-in' }

    try {
      await updatePassword(u, next)
    } catch (e) {
      const code = (e as { code?: string }).code ?? 'unknown'
      if (code === 'auth/requires-recent-login') {
        return { ok: false, step: 'reauth', code: 'needs-reauth' }
      }
      return { ok: false, step: 'update', code }
    }

    try {
      await clearMustChangePassword(u.uid)
    } catch (e) {
      return { ok: false, step: 'flag', code: (e as { code?: string }).code ?? 'unknown' }
    }

    return { ok: true }
  }

  return {
    user: readonly(user),
    claims: readonly(claims),
    mustChangePassword: readonly(mustChangePassword),
    isAuthed,
    isActive,
    isAdmin,
    email,
    login,
    logout,
    resetPassword,
    changePassword,
    changePasswordForced,
  }
}
